import { useEffect, useState } from 'react';
import { supabase } from '../../../lib/supabase';
import { useCustomer } from '../../../lib/customer';
import { useAuth } from '../../../lib/auth';
import { theme } from '../../../styles/theme';
import { PortalContainer } from '../PortalShell';

type Event = {
  id: string;
  lutron_line: string;
  title: string;
  description: string | null;
  starts_at: string;
  duration_minutes: number;
  mode: 'online' | 'presencial';
  location: string | null;
  spots_total: number;
  required_for_certification: boolean;
  invite_only: boolean;
};

type MyEnrollment = {
  event_id: string;
  status: string;
};

const ALL_LINES = ['caseta', 'radiora3', 'maestro', 'wallbox', 'vive'] as const;

export function PortalCapacitacion() {
  const { customer } = useCustomer();
  const { user } = useAuth();
  const [events, setEvents] = useState<Event[]>([]);
  const [enrollments, setEnrollments] = useState<MyEnrollment[]>([]);
  const [filter, setFilter] = useState<'all' | 'online' | 'presencial'>('all');
  const [enrollingId, setEnrollingId] = useState<string | null>(null);

  useEffect(() => {
    supabase
      .from('training_events')
      .select('*')
      .gte('starts_at', new Date().toISOString())
      .order('starts_at', { ascending: true })
      .then(({ data }) => setEvents((data as Event[]) ?? []));
  }, []);

  useEffect(() => {
    if (!user) return;
    supabase
      .from('training_enrollments')
      .select('event_id, status')
      .eq('user_id', user.id)
      .then(({ data }) => setEnrollments((data as MyEnrollment[]) ?? []));
  }, [user]);

  if (!customer) return null;

  const enroll = async (event_id: string) => {
    setEnrollingId(event_id);
    const { error } = await supabase
      .from('training_enrollments')
      .insert({ event_id, user_id: user!.id, customer_id: customer.id });
    if (error) {
      alert('No se pudo inscribir: ' + error.message);
    } else {
      setEnrollments(prev => [...prev, { event_id, status: 'enrolled' }]);
    }
    setEnrollingId(null);
  };

  const filteredEvents = events.filter(e => filter === 'all' ? true : e.mode === filter);
  const enrolledIds = new Set(enrollments.map(e => e.event_id));

  return (
    <PortalContainer title="Capacitación" eyebrow="Certificación Lutron por línea">
      {/* Aviso */}
      <div style={{
        background: theme.bgFooter,
        borderLeft: `4px solid ${theme.brandTag.lutron}`,
        padding: 16,
        marginBottom: 24,
        fontSize: 13,
        color: theme.text,
        lineHeight: 1.6,
      }}>
        <strong>Política Lutron:</strong> para vender productos Lutron a tus clientes,
        Lutron requiere que estés certificado por línea. NULED ofrece la capacitación
        sin costo adicional. Sin certificación de la línea, esa familia de productos
        no aparece en tu lista de precios.
      </div>

      {/* Status por línea */}
      <div style={{
        fontSize: 11,
        color: theme.accentDark,
        textTransform: 'uppercase',
        letterSpacing: 1.5,
        fontWeight: 700,
      }}>
        Tu certificación por línea
      </div>
      <h3 style={{ fontFamily: theme.font.head, fontSize: 22, fontWeight: 500, color: theme.brand, margin: '6px 0 16px' }}>
        Líneas que puedes vender
      </h3>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12, marginBottom: 16 }}>
        {ALL_LINES.map(line => {
          const ok = customer.lutron_lines_certified.includes(line);
          return (
            <div key={line} style={{
              background: theme.bg,
              border: `1px solid ${theme.border}`,
              borderLeft: `4px solid ${ok ? theme.accent : theme.warn}`,
              padding: 14,
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ fontFamily: theme.font.head, fontSize: 16, fontWeight: 600, color: theme.brand }}>
                  {formatLine(line)}
                </div>
                <span style={{
                  fontSize: 16,
                  color: ok ? theme.accentDark : theme.warn,
                  fontWeight: 700,
                }}>
                  {ok ? '✓' : '○'}
                </span>
              </div>
              <div style={{ fontSize: 12, color: theme.textMuted, marginTop: 6 }}>
                {ok ? 'Certificado' : 'Sin certificar — completa el curso'}
              </div>
            </div>
          );
        })}
      </div>

      {/* HomeWorks aparte */}
      <div style={{
        background: 'rgba(192,132,252,0.08)',
        border: `1px solid ${theme.brandTag.lutron}`,
        borderLeft: `4px solid ${theme.brandTag.lutron}`,
        padding: 16,
        marginBottom: 32,
        fontSize: 13,
        color: theme.text,
        lineHeight: 1.6,
      }}>
        <div style={{ fontWeight: 700, color: theme.brandTag.lutron, fontSize: 11, letterSpacing: 1, textTransform: 'uppercase', marginBottom: 4 }}>
          ★ Programa HomeWorks · Aparte
        </div>
        HomeWorks tiene su propio programa de certificación oficial Lutron. Una vez
        certificado podrás distribuir y programar la línea premium.
        Inscribe a la sesión informativa abajo.
      </div>

      {/* Calendario */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 12, flexWrap: 'wrap', gap: 12 }}>
        <h3 style={{ fontFamily: theme.font.head, fontSize: 22, fontWeight: 500, color: theme.brand, margin: 0 }}>
          Próximos eventos · Inscripciones abiertas
        </h3>
        <div style={{ display: 'flex', gap: 6 }}>
          <Tab active={filter === 'all'} onClick={() => setFilter('all')}>Todas ({events.length})</Tab>
          <Tab active={filter === 'online'} onClick={() => setFilter('online')}>Online</Tab>
          <Tab active={filter === 'presencial'} onClick={() => setFilter('presencial')}>Presencial</Tab>
        </div>
      </div>

      {filteredEvents.length === 0 && (
        <div style={{ padding: 40, textAlign: 'center', color: theme.textMuted, border: `1px dashed ${theme.border}` }}>
          Sin eventos próximos en este filtro.
        </div>
      )}

      <div style={{ display: 'grid', gap: 12 }}>
        {filteredEvents.map(e => {
          const enrolled = enrolledIds.has(e.id);
          const date = new Date(e.starts_at);
          return (
            <article key={e.id} style={{
              background: theme.bg,
              border: `1px solid ${theme.border}`,
              padding: 18,
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
              gap: 20,
              alignItems: 'center',
            }}>
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, flexWrap: 'wrap' }}>
                  <Pill bg={e.mode === 'online' ? 'rgba(14,116,144,0.1)' : 'rgba(245,158,11,0.15)'}
                        color={e.mode === 'online' ? theme.info : theme.warn}>
                    ● {e.mode === 'online' ? 'Online' : 'Presencial'}
                  </Pill>
                  <Pill bg="transparent" color={theme.brandTag.lutron}>
                    {formatLine(e.lutron_line)}
                  </Pill>
                  {e.required_for_certification && (
                    <Pill bg="transparent" color={theme.warn}>⚠ Requerida</Pill>
                  )}
                  {e.invite_only && (
                    <Pill bg="transparent" color={theme.brandTag.lutron}>★ Solo invitación</Pill>
                  )}
                </div>
                <div style={{ fontFamily: theme.font.head, fontSize: 16, fontWeight: 600, color: theme.brand, lineHeight: 1.3 }}>
                  {e.title}
                </div>
              </div>
              <div style={{ fontSize: 13, color: theme.text }}>
                <div style={{ fontWeight: 600 }}>{formatDate(date)} · {e.duration_minutes / 60}h</div>
                <div style={{ color: theme.textMuted, fontSize: 12, marginTop: 4 }}>{e.location}</div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: 11, color: theme.textMuted, marginBottom: 8 }}>
                  {e.spots_total} cupos disponibles
                </div>
                {enrolled ? (
                  <button disabled style={{ ...btnDisabled, background: theme.accent, color: theme.brand, cursor: 'default', opacity: 1 }}>
                    ✓ Inscrito
                  </button>
                ) : (
                  <button
                    disabled={enrollingId === e.id}
                    onClick={() => enroll(e.id)}
                    style={enrollingId === e.id ? btnDisabled : btnPrimary}
                  >
                    {enrollingId === e.id ? 'Inscribiendo…' : 'Inscribirme'}
                  </button>
                )}
              </div>
            </article>
          );
        })}
      </div>
    </PortalContainer>
  );
}

function Tab({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button onClick={onClick} style={{
      background: active ? theme.brand : theme.bg,
      color: active ? theme.textOnBrand : theme.brand,
      border: `1px solid ${active ? theme.brand : theme.border}`,
      padding: '8px 14px',
      fontSize: 12,
      fontWeight: 600,
      cursor: 'pointer',
      fontFamily: theme.font.sans,
    }}>
      {children}
    </button>
  );
}

function Pill({ bg, color, children }: { bg: string; color: string; children: React.ReactNode }) {
  return (
    <span style={{
      fontSize: 10,
      fontWeight: 700,
      padding: '3px 8px',
      background: bg,
      color,
      textTransform: 'uppercase',
      letterSpacing: 0.5,
      border: bg === 'transparent' ? `1px solid ${color}` : undefined,
    }}>
      {children}
    </span>
  );
}

function formatDate(d: Date) {
  return d.toLocaleDateString('es-MX', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  }) + ' · ' + d.toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' });
}

function formatLine(l: string) {
  if (l === 'radiora3') return 'RadioRA 3';
  if (l === 'caseta') return 'Caséta';
  if (l === 'homeworks') return 'HomeWorks';
  return l.charAt(0).toUpperCase() + l.slice(1);
}

const btnPrimary: React.CSSProperties = {
  background: theme.accent,
  color: theme.brand,
  border: 'none',
  padding: '10px 18px',
  fontSize: 12,
  fontWeight: 700,
  letterSpacing: 0.4,
  textTransform: 'uppercase',
  cursor: 'pointer',
  fontFamily: theme.font.sans,
};

const btnDisabled: React.CSSProperties = {
  ...btnPrimary,
  opacity: 0.5,
  cursor: 'wait',
};
